import { Suspense, lazy, useEffect } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import PrivateRoute from "./services/PrivateRouter";
import App from "./App";
import StandardMain from "./layouts/StandardMain";
import { StandardDashboard, DashboardTablePage } from "./pages/Standard";
import PageNotFound from "./components/View/PageNotFound";
import Helpindex from "./pages/help";
import HelpMain from "./layouts/HelpMain";

// Lazy loaded Route Groups
const MainpageRoutes = lazy(() => import("./routes/Mainpage.routes"));
const AdminSettingsRoutes = lazy(() => import("./routes/AdminSettings.routes"));
const AuthRoutes = lazy(() => import("./routes/Auth.routes"));
const InformationRoutes = lazy(() => import("./routes/Information.routes"));
const ActivityRoutes = lazy(() => import("./routes/Activity.routes"));
const ReferenceDocRoutes = lazy(() => import("./routes/ReferenceDoc.routes"));
const StandardSettingsRoutes = lazy(() => import("./routes/Standard.routes"));

// Lazy loaded components
const MainReports = lazy(() => import("./pages/Reports/MainReports"));
const InternalAuditReport = lazy(
  () => import("./pages/Reports/InternalAuditReport"),
);
const GenarateReport = lazy(() => import("./pages/Reports/GenarateReport"));
const PreDefinedReport = lazy(() => import("./pages/Reports/PreDefinedReport"));
const PreDefineMain = lazy(
  () => import("./pages/Reports/PreDefinedReport/PreDefineMain"),
);
const OverAllProgressIndex = lazy(
  () => import("./pages/Reports/PreDefinedReport/OverAll"),
);
const VendorManagementReport = lazy(
  () => import("./pages/Reports/PreDefinedReport/VendorManagement"),
);
const SecurityIncidentReport = lazy(
  () => import("./pages/Reports/PreDefinedReport/SecurityIncident"),
);
const TrainingCalendarReport = lazy(
  () => import("./pages/Reports/PreDefinedReport/TrainingCalendar"),
);
const AuditScheduleReport = lazy(
  () => import("./pages/Reports/PreDefinedReport/AuditSchedule"),
);
const ImplementationChecklistReport = lazy(
  () => import("./pages/Reports/PreDefinedReport/ImplementationChecklist"),
);
const InternalAuditChecklistReport = lazy(
  () => import("./pages/Reports/PreDefinedReport/InternalAuditChecklist"),
);
const MaintenanceReport = lazy(
  () => import("./pages/Reports/PreDefinedReport/Maintenance"),
);
const MasterListofDocumentsReport = lazy(
  () => import("./pages/Reports/PreDefinedReport/MasterListofDocuments"),
);
const MRMReport = lazy(() => import("./pages/Reports/PreDefinedReport/MRM"));
const ConsentManagementReport = lazy(
  () => import("./pages/Reports/PreDefinedReport/Consent Management"),
);
const SLAManagementReport = lazy(
  () => import("./pages/Reports/PreDefinedReport/SLA Management"),
);

// Loading fallback component
const PageLoader = () => (
  <div className="flex h-screen w-full items-center justify-center">
    <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-blue-500"></div>
  </div>
);

function Mainroutes() {
  const location = useLocation();

  useEffect(() => {
    if (
      location.pathname === "/" ||
      location.pathname === "" ||
      location.pathname === "main"
    ) {
      window.location.replace("/main/standard");
    }
    if (location.pathname === "/auth") {
      window.location.replace("/auth/login");
    }
  }, [location]);
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route
          path="/"
          element={
            <PrivateRoute>
              <App />
            </PrivateRoute>
          }
        >
          <Route path="main/*" element={<MainpageRoutes />} />
          <Route path="admin-settings/*" element={<AdminSettingsRoutes />} />
          <Route path="standard/:stdId" element={<StandardMain />}>
            <Route path="dashboard" element={<StandardDashboard />} />
            <Route path="dashboard/tables" element={<DashboardTablePage />} />
            <Route path="reference-doc/*" element={<ReferenceDocRoutes />} />
            <Route path="information/*" element={<InformationRoutes />} />
            <Route path="activity/*" element={<ActivityRoutes />} />
            <Route path="settings/*" element={<StandardSettingsRoutes />} />
            <Route path="report" element={<MainReports />}>
              <Route
                path="internal-audit-report"
                element={<InternalAuditReport />}
              />
              <Route path="generate-report" element={<GenarateReport />} />
              <Route path="pre-defined-report" element={<PreDefinedReport />} />
              <Route path="pre-defined" element={<PreDefineMain />}>
                <Route
                  path="overallprogress"
                  element={<OverAllProgressIndex />}
                />
                <Route
                  path="vendor-management-report"
                  element={<VendorManagementReport />}
                />
                <Route
                  path="security-incident-report"
                  element={<SecurityIncidentReport />}
                />
                <Route
                  path="training-calendar-report"
                  element={<TrainingCalendarReport />}
                />
                <Route
                  path="consent-management-report"
                  element={<ConsentManagementReport />}
                />
                <Route
                  path="sla-management-report"
                  element={<SLAManagementReport />}
                />
                <Route
                  path="implementationckeck-list-report"
                  element={<ImplementationChecklistReport />}
                />
                <Route
                  path="internal-audit-report"
                  element={<InternalAuditChecklistReport />}
                />
                <Route
                  path="maintenance-report"
                  element={<MaintenanceReport />}
                />
                <Route
                  path="master-report"
                  element={<MasterListofDocumentsReport />}
                />
                <Route path="mrm-report" element={<MRMReport />} />
                <Route
                  path="audit-schedule-report"
                  element={<AuditScheduleReport />}
                />
              </Route>
            </Route>
          </Route>
          <Route path="help" element={<HelpMain />}>
            <Route index element={<Helpindex />} />
            <Route path=":stdId" element={<Helpindex />} />
          </Route>
        </Route>
        <Route path="auth/*" element={<AuthRoutes />} />
        <Route path="/*" element={<PageNotFound />} />
      </Routes>
    </Suspense>
  );
}

export default Mainroutes;
